/**
 * textConstants.js
 *
 * This file contains all the text labels, titles, and other static text constants used across the application.
 * The constants are organized by views, components, and sub-components for better modularity and ease of maintenance.
 * Each export represents a separate view or component, and inside each object, the labels are organized as follows
 * (suggested structure, subject to change):
 *
 *    Example (View):
 *    export const VIEW_NAME = {
 *      VIEW_LABEL: 'Label for the view',
 *      VIEW_LABEL_FOR_A_SPECIFIC_SECTION: { PHY: 'Label for PHY', APP: 'Label for APP' },
 *      COMPONENT: {
 *        COMPONENT_SPECIFIC_LABEL: 'Label for a specific component within the view'
 *      },
 *      SUB_VIEW: {
 *        SUB_VIEW_LABEL: 'Label for a sub-view or sub-section'
 *      }
 *    };
 *
 *    Example (common component):
 *    export const COMMON_COMPONENT_NAME = {
 *      LABEL: 'Label for a common component',
 *      ANOTHER_LABEL: 'Another label for a common component'
 *    };
 */

// Common components
export const SESSION_TIMEOUT_MODAL = {
  TITLE: 'Session Timeout',
  CONTENT: `You will be automatically logged off due to inactivity in $TIME_LEFT minutes. Please choose to either stay signed in by clicking the <strong>'Stay Signed In'</strong> button or log off by clicking the <strong>'Log Off'</strong> button.`,
  CTA: {
    STAY: 'Stay Signed In',
    LOGOFF: 'Log Off',
  },
}

export const OPPORTUNITY_ASSESSMENT_FILTERS = {
  WORK_WRVU_ASA_UNITS: 'Calculated with wRVUs or ASA Units',
  COLLECTIONS: 'Calculated with Collections',
  EMPTY_MESSAGE: 'Please select an opportunity assessment to see data',
  MODAL_LINK: 'Opportunity Rules Definitions',
}

export const OPPORTUNITY_ASSESSMENT_MODAL = {
  TITLE: 'Opportunity Assessment Rules Definitions',
  TABLE: {
    HEADER: {
      RULE: 'Opportunity Assessment Rule',
      DESCRIPTION: 'Rule Description',
      DEFINITION: 'Rule Definition',
    },
    PHYSICIAN: [
      {
        rule: 'Alignment TCC \u003e P20',
        description: 'Clinical compensation is generally aligned with productivity relative to the market benchmarks.\u00b9',
        definition: 'Clinical TCC above 20th \u0025ile and productivity alignment within 11 percentile points.',
      },
      {
        rule: 'Risk Retention',
        description:
          'Clinical compensation is generally low relative to the market, while productivity is disproportionately high. Indicates a potential risk of attrition.',
        definition:
          'Clinical TCC below 45th \u0025ile, productivity above 50th \u0025ile OR Clinical TCC below the 75th \u0025ile; productivity above the 55th \u0025ile.',
      },
      {
        rule: 'Misalignment (High Pay)',
        description: 'High level of clinical compensation relative to the market and disproportionately high compared to productivity.',
        definition: 'Clinical TCC above the 50th \u0025ile (\u003e11\u0025 gap)',
      },
      {
        rule: 'Misalignment (Moderate Pay)',
        description: 'Moderate level of clinical compensation relative to the market and disproportionately high compared to productivity.',
        definition: 'Clinical TCC above the 30th \u0025ile; wRVUs below the 40th \u0025ile',
      },
      {
        rule: 'Low Productivity Issues',
        description: 'Represents an area where productivity is low relative to market benchmarks.',
        definition: 'Clinical TCC below the 30th \u0025ile; productivity below 20th \u0025ile',
      },
      {
        rule: 'All Remaining Physicians',
        description: 'No rules above met.',
        definition: 'No rules above met',
      },
    ],
    APP: [
      {
        rule: 'Highly Experienced With Low TCC (Excluding Premium)',
        description: 'Clinical compensation is low relative to the provider\u2019s significant experience as an APP.',
        definition:
          'Years of experience \u003e\u003d 10 and TCC (excluding Premium) \u0025ile \u003c\u003d 25. For CRNAs, Peds CRNAs, and CAAs \u003e\u003d5 years of experience and TCC (Excluding Premium) \u0025ile \u003c\u003d 25.',
      },
      {
        rule: 'Recent Graduates With Low TCC (Excluding Premium)',
        description: 'Clinical compensation is low relative to the provider\u2019s experience level as an early-career or recently graduated APP.',
        definition: 'Years of experience is 0\u20133 and TCC (Excluding Premium) \u0025ile \u003c\u003d 15.',
      },
      {
        rule: 'TCC (Excluding Premium) and Productivity Alignment',
        description: 'Clinical compensation is generally aligned with productivity relative to the market benchmarks.\u00b9',
        definition:
          'TCC (Excluding Premium) and Productivity Alignment: at or above the 75th percentile, with a 25 percentile-point alignment between TCC (excluding premium) and productivity (for the following specialties: Certified Nurse Midwife, Family Medicine, Family Medicine with Obstetrics, Geriatrics, Internal Medicine, Pediatrics-Adolescent Medicine, Pediatrics-General, Pediatrics-Internal Medicine, Dermatology, Urgent Care, and Hospitalist).',
      },
      {
        rule: 'Highly Paid APPs',
        description: 'Represents an area where compensation is high relative to market benchmarks.',
        definition: 'APP with TCC (Excluding Premium) \u0025ile 30\u0025 over P90.',
      },
      {
        rule: 'Low Productivity Issues',
        description: 'Represents an area where productivity is low relative to market benchmarks.',
        definition: 'wRVUs or ASA Units \u0025ile \u003c\u003d 25 for specialty except Hospital Based.',
      },
      {
        rule: 'All Remaining APPs',
        description: 'No rules above met.',
        definition: 'No rules above met',
      },
    ],
    FOOTER:
      '\u00b9 Productivity market benchmarks reflect personally-provided professional services, either represented by wRVUs, ASA Units, or Net Collections.',
  },
}

// Views
export const COMPILE_BLEND_AND_AGE = {
  BREADCRUMBS: 'Manage Data / COMPILE Market Data / Blend and Age',
  SIDEPANE: {
    TITLE: '<b>COMPILE</b> Blend and Age',
    MARKET_DATA: {
      SECTION_TITLE: '<b>BLEND</b> Market Data',
      ACCORDION_TITLE: 'Market Data Source $IDX',
    },
    MARKET_DATA_SOURCE_1: 'Market Data Source 1',
    MARKET_DATA_SOURCE_2: 'Market Data Source 2',
    MARKET_DATA_SOURCE_3: 'Market Data Source 3',
    DATEPICKER: {
      SECTION_TITLE: '<b>AGE</b> Market Data',
      PLACEHOLDER: 'Age TCC',
      ERROR_MSG: 'Enter a valid date in mm/dd/yyyy format to age the market TCC data',
    },
    ADD_MARKET_DATA_SOURCE: 'Add Market Data',
    CONFIRMATION_CTA: 'Apply',
  },
  INFO_MESSAGE: `To age a Market Data Source, please select <b>1 Market Data Source and an Age TCC date.</b>
    <br />To visualize a Market Blend, please select at least <b>2 Market Data Sources.</b>
    <br />If you choose not to age any Market Data Source,  Total Cash Compensation (TCC) benchmark data will reflect the effective date of the survey.`,
  INFO_MESSAGE_MULTI_SOURCE: `To age a Market Data Source, please select <b>1 Market Data Source and an Age TCC date.</b>
    <br />To visualize a Market Blend, please select at least <b>2 Market Data Sources</b> (maximum of 12).
    <br />If you choose not to age any Market Data Source,  Total Cash Compensation (TCC) benchmark data will reflect the effective date of the survey.`,
  TOASTS: {
    ERROR: {
      TITLE: `There was an error, try again later.`,
    },
    NOT_UNIQUE: {
      TITLE: 'Market Data Source Error',
      INFO: 'Please select different Market Data Sources',
    },
    NO_RESULTS: {
      TITLE: 'Market Data Source',
      INFO: 'No results found for the selected filters',
    },
    CAN_NOT_SAVE: {
      TITLE: `Blend can't be saved.`,
    },
    SUCCESS: {
      TITLE: 'BLEND_NAME saved successfully',
      INFO: 'The full dataset has been saved and it is now available in your Market Library.',
    },
    EXPORT_SUCCESS: {
      TITLE: 'Exported data',
      INFO: 'The data was exported successfully',
    },
    EXPORT_ERROR: {
      TITLE: 'Exported data',
      INFO: 'There was an error exporting the data',
    },
  },
}

export const COMPILE_CUSTOM_MODULE = {
  BREADCRUMBS: 'Manage Data / COMPILE Market Data / Blend and Age',
  SIDEPANE: {
    TITLE: '  <b>COMPILE</b> by Survey Characteristic',
    MARKET_DATA_SOURCE: 'Market Data Source',
    MARKET_SURVEY_CUT: 'Survey Characteristic',
    MARKET_SURVEY_NO_CUT: 'No characteristics available',
    APPLY_BTN: 'Apply',
  },
  INFO_MESSAGE: `To create Survey Characteristics, please select <b>1 Market Data Source</b> and <b>1 Survey Characteristic</b>`,
  TOASTS: {
    ERROR: {
      TITLE: `There was an error, try again later.`,
    },
    NO_RESULTS: {
      TITLE: 'Market Data Source',
      INFO: 'No results found for the selected filters',
    },
    SAVE_SUCCESS: {
      TITLE: 'CUSTOM_NAME saved successfully',
      INFO: 'Compile by Survey Characteristic has been saved and it is now available in your Market Library.',
    },
    SAVE_ERROR: {
      TITLE: 'Failed to save CUSTOM_NAME',
      INFO: 'An error occurred while saving the module. Please try again.',
    },
    EXPORT_SUCCESS: {
      TITLE: 'Exported data',
      INFO: 'The data was exported successfully',
    },
    EXPORT_ERROR: {
      TITLE: 'Exported data',
      INFO: 'There was an error exporting the data',
    },
  },
}

export const MARKET_POSITIONING_INCUMBENT = {
  SIDEPANE: {
    TITLE: '<b>Filters</b> / Incumbent',
    MARKET_DATA_SOURCE: 'Market Data Source',
    ORG_DATA_SOURCE: 'Organization Data Source',
    SPECIALTY_GROUP: 'Specialty Group',
    SPECIALTY: 'Specialty',
    INCUMBENT_NAME: 'Incumbent Name or Unique Identifier',
    OPP_ASSESSMENT: 'Opportunity Assessment',
    APPLY_BUTTON: 'Apply',
  },
  TABLE: {
    EMPTY_STATE: 'To visualize an Incumbent table, please select <b>1 Market Data Source</b> and <b>1 Organization Data Source</b>.',
  },
  INCUMBENT: {
    SCATTERPLOT: {
      TITLE: {
        PHY: {
          WRVUS: 'Clinical Compensation v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'Clinical Compensation v. Collections - Percentile Ranks',
        },
        APP: {
          WRVUS: 'TCC (Excluding Premium) v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'TCC (Excluding Premium) v. Collections - Percentile Ranks',
        },
      },
      INCUMBENT_COUNT: 'Incumbents: $COUNT',
      X_AXIS_DROPDOWN: { LABEL: 'X-Axis' },
      X_AXIS: { WRVUS: 'wRVUs or ASA Units: Market %ile', COLLECTIONS: 'Collections: Market %ile' },
      Y_AXIS: { PHY: 'Clinical Compensation: Market %ile', APP: 'TCC (Excluding Premium): Market %ile' },
      POINT_LABELS: {
        NAME: 'Name/ID:',
        SPECIALTY: 'Specialty:',
        COMPENSATION: { PHY: 'Clinical Compensation Market %ile:', APP: 'TCC (Excluding Premium) Market %ile:' },
        WRVU: 'wRVUs or ASA Units Market %ile:',
        COLLECTIONS: 'Collections Market %ile:',
      },
      DATASET_NAME: 'Incumbent',
    },
  },
  OPP_ASSESSMENT: {
    SCATTERPLOT: {
      TITLE: {
        PHY: {
          WRVUS: 'Clinical Compensation v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'Clinical Compensation v. Collections - Percentile Ranks',
        },
        APP: {
          WRVUS: 'TCC (Excluding Premium) v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'TCC (Excluding Premium) v. Collections - Percentile Ranks',
        },
      },
      INCUMBENT_COUNT: 'Incumbents: $COUNT',
      X_AXIS: 'wRVUs or ASA Units: Market %ile',
      Y_AXIS: { PHY: 'Clinical Compensation: Market %ile', APP: 'TCC (Excluding Premium): Market %ile' },
      POINT_LABELS: {
        NAME: 'Name/ID:',
        SPECIALTY: 'Specialty:',
        COMPENSATION: { PHY: 'Clinical Compensation Market %ile:', APP: 'TCC (Excluding Premium) Market %ile:' },
        WRVU: 'wRVUs or ASA Units Market %ile:',
      },
    },
    TREEMAP: {
      TITLE: 'Opportunity Assessment',
    },
  },
  NO_RESULTS: 'No data is available for the selected filter. Please select a different filter combination to view the Market Positioning',
  EXPORT_BREADCRUMBS: 'Market Positioning / Incumbent / $VIEW',
}

export const MARKET_POSITIONING_SPECIALTY = {
  SIDEPANE: {
    TITLE: '<b>Filters</b> / Specialty',
    MARKET_DATA_SOURCE: 'Market Data Source',
    ORG_DATA_SOURCE: 'Organization Data Source',
    SPECIALTY_GROUP: 'Specialty Group',
    SPECIALTY: 'Specialty',
    OPP_ASSESSMENT: 'Opportunity Assessment',
    APPLY_BUTTON: 'Apply',
  },
  TABLE: {
    EMPTY_STATE: 'To visualize a Specialty table, please select <b>1 Market Data Source</b> and <b>1 Organization Data Source</b>.',
  },
  SPECIALTY: {
    SCATTERPLOT: {
      TITLE: {
        PHY: {
          WRVUS: 'Clinical Compensation v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'Clinical Compensation v. Collections - Percentile Ranks',
        },
        APP: {
          WRVUS: 'TCC (Excluding Premium) v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'TCC (Excluding Premium) v. Collections - Percentile Ranks',
        },
      },
      X_AXIS_DROPDOWN: { LABEL: 'X-Axis' },
      X_AXIS: { WRVUS: 'wRVUs or ASA Units: Market %ile', COLLECTIONS: 'Collections: Market %ile' },
      Y_AXIS: { PHY: 'Clinical Compensation: Market %ile', APP: 'TCC (Excluding Premium): Market %ile' },
      POINT_LABELS: {
        NAME: 'Name/ID:',
        SPECIALTY: 'Specialty:',
        COMPENSATION: { PHY: 'Clinical Compensation Market %ile:', APP: 'TCC (Excluding Premium) Market %ile:' },
        WRVU: 'wRVU or ASA Unit Market %ile:',
        COLLECTIONS: 'Collections Market %ile:',
      },
      DATASET_NAME: 'Specialty',
    },
  },
  OPP_ASSESSMENT: {
    SCATTERPLOT: {
      TITLE: {
        PHY: {
          WRVUS: 'Clinical Compensation v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'Clinical Compensation v. Collections - Percentile Ranks',
        },
        APP: {
          WRVUS: 'TCC (Excluding Premium) v. wRVUs or ASA Units - Percentile Ranks',
          COLLECTIONS: 'TCC (Excluding Premium) v. Collections - Percentile Ranks',
        },
      },
      X_AXIS: { WRVUS: 'wRVUs or ASA Units: Market %ile', COLLECTIONS: 'Collections: Market %ile' },
      Y_AXIS: { PHY: 'Clinical Compensation: Market %ile', APP: 'TCC (Excluding Premium): Market %ile' },
      POINT_LABELS: {
        SPECIALTY: 'Specialty:',
        COMPENSATION: { PHY: 'Clinical Compensation Market %ile:', APP: 'TCC (Excluding Premium) Market %ile:' },
        WRVU: 'wRVU or ASA Unit Market %ile:',
      },
    },
    TREEMAP: {
      TITLE: 'Opportunity Assessment',
    },
  },
  NO_RESULTS: 'No data is available for the selected filter. Please select a different filter combination to view the Market Positioning',
  EXPORT_BREADCRUMBS: 'Market Positioning / Specialty / $VIEW',
}

export const MARKET_POSITIONING_SPECIALTY_GROUP = {
  SIDEPANE: {
    TITLE: '<b>Filters</b> / Specialty Group',
    MARKET_DATA_SOURCE: 'Market Data Source',
    ORG_DATA_SOURCE: 'Organization Data Source',
    SPECIALTY_GROUP: 'Specialty Group',
    APPLY_BUTTON: 'Apply',
  },
  TABLE: {
    EMPTY_STATE: 'To visualize a Specialty Group table, please select <b>1 Market Data Source</b> and <b>1 Organization Data Source</b>.',
  },
  BARCHART: {
    TITLE: 'Market Positioning by Specialty Group - cFTE',
    Y_AXIS: 'Market %ile',
    BAR_LABEL: '$NAME (n=$TOTAL)',
  },
  NO_RESULTS: 'No data is available for the selected filter. Please select a different filter combination to view the Market Positioning',
  EXPORT_BREADCRUMBS: 'Market Positioning / Specialty Group / $VIEW',
}

export const MARKET_MODELING = {
  SIDEPANE: {
    TITLE_PREFIX: 'Filters',
    TITLE_SUFFIX: 'Market Modeling',
    ORG_DATA_SOURCE: 'Organization Data Source',
    APPLY_BUTTON: 'Apply',
  },
  CONTENT: {
    TITLE: 'Market Modeling',
    SUBTITLE: 'Target Productivity Percentiles',
    MESSAGE_BAR:
      "To perform market modeling, please select the organization data source you'd like to analyze for the below scenarios and click apply.",
    EMPTY_STATE: 'No target productivity percentile data available.',
  },
  TABLE: {
    APP_SEGMENT_LABEL: 'Practice Setting',
    PHY_SEGMENT_LABEL: 'Specialty Group',
  },
  OVERVIEW: {
    LEVEL_LABEL: 'Level',
    VIEW_LABEL: 'View',
    LEVELS: {
      SPECIALTY_GROUP: 'Specialty Group',
      SPECIALTY: 'Specialty',
      INCUMBENT: 'Incumbent',
    },
    VIEWS: {
      OVERVIEW: 'Overview',
    },
    CHARTS: {
      COMPENSATION_TITLE: 'Current Compensation Percentiles',
      WORK_RVUS_TITLE: 'Current Work RVUs Percentiles',
      COLLECTIONS_TITLE: 'Current Collections Percentiles',
      INCUMBENT_COUNT_AXIS: 'Incumbent Count',
      INCUMBENT_DATASET: 'Incumbents',
    },
  },
}

export const UPLOAD = {
  TEXT_BUTTON: {
    ORGANIZATION_DATA: 'Organization Data',
    MARKET_DATA: 'Market Data',
  },
  TITLE: {
    ORGANIZATION_DATA: 'Upload Organization Data',
    MARKET_DATA: 'Upload Market Data',
  },
  CHOOSE_INFO: {
    ORGANIZATION_DATA:
      'Choose a data file to upload to your Organization Library. You can use a template or submit your own file. Make sure to use Excel files only.',
    MARKET_DATA:
      'Choose a data file to upload to your Market Library. You can use a template or submit your own file. Make sure to use Excel files only.',
  },
  DOWNLOAD_TEMPLATE: 'Download Template',
  ADD_A_FILE: 'Add a file',
  DRAG_AND_DROP_OR: 'Drag and drop data file or',
  CLICK_TO_BROWSE: 'click to browse',
  SOME_FILES_WHERE_REJECTED: 'Some files were rejected',
  FILE_SIZE: 'Up to 10 MB. Use only Excel file',
  DATE_MESSAGE: {
    ORGANIZATION_DATA: 'Choose Time Period Start Date',
    MARKET_DATA: 'Choose an effective date for your Market Data',
  },
  DATE_PICKER_ERROR: {
    ORGANIZATION_DATA: 'Enter a valid date in mm/dd/yyyy format to upload organization data.',
    MARKET_DATA: 'Enter a valid date in mm/dd/yyyy format to upload market data.',
  },
  DATE_PICKER_BOTTOM_TEXT: {
    ORGANIZATION_DATA: 'Submit incumbent data reflecting a complete fiscal year (12 months).',
  },
  FACULTY_RANK: 'Check this box if you will use the Faculty Rank column in this upload',
  SUBMIT: 'Submit',
  DISCLAIMER: 'DISCLAIMER',
  DISCLAIMER_TEXT: {
    ORGANIZATION_DATA: `Data uploaded to Benchmarks360<sup class="-top-1 mr-0">TM</sup> is the sole responsibility of the user. 
    SullivanCotter cannot accept responsibility for the content, accuracy, and integrity of user uploaded market or organization data. 
    The validation performed when uploading data reviews file type and field formatting only. 
    The SullivanCotter Health Check<sup class="-top-1 mr-0">TM</sup> capability and results are for informational purposes only to help users understand and 
    improve the accuracy of their data and does not formally indicate data inaccuracies. © 2025 by SullivanCotter. All rights reserved.`,
    MARKET_DATA: `Data uploaded to Benchmarks360<sup class="-top-1 mr-0">TM</sup> is the sole responsibility of the user. 
    SullivanCotter cannot accept responsibility for the content, accuracy, and integrity of user uploaded market or organization data.
    The validation performed when uploading data reviews file type and field formatting only. © 2025 by SullivanCotter. All rights reserved.`,
  },
  UPLOAD_STATUS: {
    CLOSE_UPLOADS_STATUS: 'Close Uploads Status',
    CLEAR_ALL_COMPLETED: 'Clear all completed',
    NO_FILES: 'No files under submission at the moment.',
    UPLOAD_DATE: 'Upload Date',
    VIEW_ERRORS: 'View errors',
  },
  UPLOAD_INFO: {
    VIEW_UPLOADS_STATUS: 'View Uploads Status',
    UPLOAD_PROCESS_DESCRIPTION: 'Here is what you can expect from the upload file process:',
    STEP_1: {
      TITLE: 'Submit',
      DESCRIPTION: 'Upon submission, the system will verify your unique file name and ensure that it does not already exist in your library.',
    },
    STEP_2: {
      TITLE: 'Validation',
      DESCRIPTION: 'After submission, the system will validate each column, row, and field formatting requirements.',
    },
    STEP_3: {
      TITLE: 'SullivanCotter Health Check',
      DESCRIPTION: 'After validation, the file will go through the SullivanCotter Health Check',
      MORE_INFO: 'For more information, please review the',
    },
    HEALTH_CHECK_GUIDELINES_LINK: 'Health Check Guidelines.',
  },
  ERRORS: {
    FILE_TYPE: 'Invalid file type. Please upload an Excel file.',
    ONE_FILE: 'Only one file can be uploaded at a time.',
    FILE_SIZE: 'Data file exceeds 10 MB limit. Try uploading a file less than 10 MB.',
    MISSING_DATA: 'Please select a file and enter an effective date.',
  },
}

export const ADMIN = {
  NAV: {
    MENU: {
      ADMIN: 'Admin',
    },
    TABS: {
      TEMPLATES: 'Templates',
      MESSAGES_TOOLTIPS: 'Messages & Tooltips',
      MARKET_LIBRARY: 'Market Library',
    },
    MOCK_TILES: {
      TEMPLATES: 'Admin Templates',
      MESSAGES: 'Admin Messages',
      MARKET_LIBRARY: 'Admin Market Library',
    },
  },
  ADMIN_TEMPLATES: {
    TITLE: 'Templates',
    SEARCH_LABEL: 'Search',
    SEARCH_PLACEHOLDER: 'Search templates...',
    EMPTY_STATE: 'No templates found.',
    NOT_AVAILABLE: '-',
    DATE_FORMAT: 'MM/DD/YYYY hh:mm A',
    TABLE_HEADERS: {
      TEMPLATE_NAME: 'Template Name',
      TYPE: 'Type',
      LAST_MODIFIED: 'Last Modified',
      ACTIONS: 'Actions',
    },
    ACTIONS: {
      MORE_OPTIONS: 'More options',
      DOWNLOAD_CURRENT_TEMPLATE: 'Download current template',
      UPLOAD_REPLACEMENT_TEMPLATE: 'Upload replacement template',
    },
    MORE_OPTIONS_MENU: {
      ARIA_LABEL: 'Template actions',
      EDIT: {
        KEY: 'edit',
        LABEL: 'Edit',
      },
      DOWNLOAD: {
        KEY: 'download',
        LABEL: 'Download',
      },
    },
    TOASTS: {
      SERVER_ERROR_TITLE: 'Server error',
      TEMPLATE_REPLACED: {
        TITLE: 'Template replaced',
        DESCRIPTION: 'The current template was replaced successfully and is now available for download.',
      },
      ERRORS: {
        GET_TEMPLATES: 'Error getting templates',
        REPLACE_TEMPLATE: 'Error replacing template',
        UPLOAD_TEMPLATE: 'Error uploading template',
        DOWNLOAD_TEMPLATE: 'Error downloading template',
      },
    },
    DOWNLOAD: {
      DEFAULT_FILE_PREFIX: 'template',
    },
  },
}

export const AT_GLANCE_DASHBOARD = {
  TITLE: {
    PHYSICIAN: 'Physician Details',
    ADVANCE_PRACTICE_PROVIDER: 'Advanced Practice Provider Details',
  },
  DESCRIPTION: 'At-A-Glance is intended to provide you with a perspective for clinically oriented providers (Staff Level).',
  PROVIDER_INFORMATION_SECTION: {
    ERROR: { INVALID_SPECIALTY: 'Invalid Specialty' },
    TITLE: 'Provider Information',
    NAME_LABEL: 'Name',
    NPI_OR_ID_LABEL: 'NPI or ID',
    SPECIALTY_LABEL: 'Specialty',
  },
  DATA_VARIABLE_SECTION: {
    TITLE: 'Data Variables',
    COMPENSATION_TCC_LABEL: 'Compensation (TCC)',
    COMPENSATION_TCC_EXCLUDING_PREMIUM_LABEL: 'Compensation (TCC - Excluding Premium)',
    COMPENSATION_TCC_PLACEHOLDER: 'Enter Compensation TCC',
    COMPENSATION_TCC_EXCLUDING_PREMIUM_PLACEHOLDER: 'Enter Compensation TCC - Excluding Premium',
    TOTAL_FTE_LABEL: 'Total FTE',
    TOTAL_FTE_PLACEHOLDER: 'Enter FTE',
    PRODUCTIVITY_LABEL: 'Productivity ({wrvusOrAsaUnitsLabel})',
    PRODUCTIVITY_PLACEHOLDER: 'Enter {wrvusOrAsaUnitsLabel}',
    CLINICAL_FTE_LABEL: 'Clinical FTE (cFTE)',
    CLINICAL_FTE_PLACEHOLDER: 'Enter cFTE',
  },
  NOTES_SECTION: {
    TITLE: 'Notes',
    NOTES_LABEL: '',
    NOTES_PLACEHOLDER: 'Enter Notes',
  },
  TOASTS: {
    ERROR: {
      TITLE: 'There was an error, try again later.',
    },
    WARNING_DATASOURCE_ID: {
      TITLE: 'Based on the specialty and data source selections, data is unavailable to display at least one of the requested graphs.',
    },
    SELECTION_MISMATCH: {
      TITLE: 'Selection mismatch',
      INFO: 'Specialty selection cannot be found in the data source. Please choose a different combination of specialty and data source.',
    },
  },
  ERROR: {
    NAME_REQUIRED: 'Name is required',
    NAME_MAX_LENGTH: 'Name must be 70 characters or less',
    NPI_OR_ID_MAX_LENGTH: 'NPI or ID must be 50 characters or less',
    SPECIALTY_REQUIRED: 'Specialty selection is required',
    DATA_SOURCE_REQUIRED: 'A Data Source selection is required',
    DATA_SOURCE_MAX: 'You can add up to 4 data sources',
    CATEGORY_REQUIRED: 'A Category selection is required',
    COMPENSATION_LENGTH_EXCEEDED: 'Compensation must be 10 characters or less',
    TOTAL_FTE_REQUIRED: 'Total FTE is required',
    COMPENSATION_TCC_REQUIRED: 'Compensation (TCC) is required',
    CLINICAL_FTE_OVER_TOTAL: 'Clinical FTE (cFTE) must be less than or equal to Total FTE',
    PRODUCTIVITY_REQUIRED: 'Productivity ({wrvusOrAsaUnitsLabel}) is required',
    CLINICAL_FTE_REQUIRED: 'Clinical FTE (cFTE) is required',
    PRODUCTIVITY_LENGTH_EXCEEDED: 'Productivity ({wrvusOrAsaUnitsLabel}) must be 5 characters or less',
    PRODUCTIVITY_NUMERIC_RANGE: 'Productivity ({wrvusOrAsaUnitsLabel}) must be numeric and between 0.0 and 99999 inclusive',
    TOTAL_FTE_MAX_LENGTH: 'Total FTE must be 3 characters or less',
    CLINICAL_FTE_MAX_LENGTH: 'Clinical FTE (cFTE) must be 3 characters or less',
    NOTES_MAX_LENGTH: 'Notes cannot have more than 200 characters',
    LEADERSHIP_BASE_COMPENSATION_REQUIRED: 'Leadership Base Compensation is required',
    LEADERSHIP_FTE_REQUIRED: 'Leadership FTE is required',
    ACADEMIC_BASE_COMPENSATION_REQUIRED: 'Academic Base Compensation is required',
    ACADEMIC_FTE_REQUIRED: 'Academic FTE is required',
    TOTAL_FTE_EXCEEDS_ONE: 'The sum of FTEs cannot exceed 1.0',
    LEADERSHIP_TITLE_MAX_LENGTH: 'Leadership Title must be 50 characters or less',
    ACADEMIC_RANK_REQUIRED: 'Academic Rank selection is required',
    LEADERSHIP_LEVEL_REQUIRED: 'Leadership Level selection is required',
    PAYBACK_TERM_NUMERIC_RANGE: 'Payback Term is required when Sign-On or Retention Bonus is provided',
    VALUE_GREATER_THAN_0: 'Value must be greater than 0',
    VALUE_GREATER_THAN: 'Value must be greater or equal than ',
    VALUE_LESS_THAN_OR_EQUAL: 'Value must be less than or equal to ',
    VALUE_IS_REQUIRED: 'Value is required',
  },
  SUBMIT: 'Submit',
  RESET: 'Reset',
}

export const AT_GLANCE_ENHANCED = {
  INFO_DISCLAIMER: {
    TITLE: withinGuidelines => {
      if (withinGuidelines) return 'Within Identified Parameters'
      return 'Needs further review'
    },
  },
  TITLE: 'Market Assessment for $NAME',
  PLEASE_ENTER_PHYSICIAN_DETAILS: 'To evaluate a provider for compliance, please enter their inputs into the side pane and click apply.',
  PHYSICIAN_BACKGROUND: {
    TITLE: 'Physician Background',
    NAME_LABEL: 'Name',
    NAME_PLACEHOLDER: 'Enter Name',
    DATE_OF_HIRE_LABEL: 'Date of Hire',
    DATE_OF_HIRE_PLACEHOLDER: 'mm/dd/yyyy',
    DATE_OF_HIRE_ERROR: 'Enter a valid date in mm/dd/yyyy format',
    SPECIALTY_LABEL: 'Specialty',
    SPECIALTY_PLACEHOLDER: 'Select Specialty',
    INVALID_SPECIALTY: 'Invalid Specialty',
    DATA_SOURCE_LABEL: 'Market Data Source',
    DATA_SOURCE_PLACEHOLDER: 'Select Market Data Source',
    DATA_SOURCE_REQUIRED: 'A Data Source selection is required',
    BASE_SALARY_LABEL: 'Base Salary/Draw',
    BASE_SALARY_OR_DRAW_PLACEHOLDER: 'Enter Base Salary/Draw',
    CLINICAL_FTE_LABEL: 'Clinical FTE',
    CLINICAL_FTE_PLACEHOLDER: 'Enter Clinical FTE',
  },
  WORK_EFFORT_ALLOCATIONS: {
    TITLE: 'Work Effort Allocations',
    LEADERSHIP_FTE_LABEL: 'Leadership FTE',
    LEADERSHIP_FTE_PLACEHOLDER: 'Enter Leadership FTE',
    LEADERSHIP_LEVEL_LABEL: 'Leadership Level',
    LEADERSHIP_LEVEL_PLACEHOLDER: 'Select Leadership Level',
    LEADERSHIP_TITLE_LABEL: 'Leadership Title',
    LEADERSHIP_TITLE_PLACEHOLDER: 'Enter Leadership Title',
    ACADEMIC_FTE_LABEL: 'Academic FTE',
    ACADEMIC_FTE_PLACEHOLDER: 'Enter Academic FTE',
    ACADEMIC_RANK_LABEL: 'Academic Rank',
    ACADEMIC_RANK_PLACEHOLDER: 'Select Academic Rank',
  },
  CLINICAL_COMPENSATION: {
    TITLE: 'Clinical Compensation',
    PRODUCTIVITY_BASED_COMPENSATION_LABEL: 'Productivity-Based Compensation',
    PRODUCTIVITY_BASED_COMPENSATION_PLACEHOLDER: 'Enter Productivity-Based Compensation',
    QUALITY_INCENTIVE_LABEL: 'Quality Incentive',
    QUALITY_INCENTIVE_PLACEHOLDER: 'Enter Quality Incentive',
    TOTAL_PAID_APP_SUPERVISION_COMPENSATION_LABEL: 'Total Paid APP Supervision Compensation',
    TOTAL_PAID_APP_SUPERVISION_COMPENSATION_PLACEHOLDER: 'Enter Total Paid APP Supervision Compensation',
    NUMBER_OF_APPS_SUPERVISED_LABEL: 'Number of APPs Supervised',
    NUMBER_OF_APPS_SUPERVISED_PLACEHOLDER: 'Enter Number of APPs Supervised',
    SIGN_ON_RETENTION_BONUS_LABEL: 'Sign-On/Retention Bonus',
    SIGN_ON_RETENTION_BONUS_PLACEHOLDER: 'Enter Sign-On/Retention Bonus',
    PAYBACK_TERM_LABEL: 'Payback term',
    PAYBACK_TERM_PLACEHOLDER: 'Enter Payback term',
    EXTRA_SHIFT_COMPENSATION_LABEL: 'Extra Shift Compensation',
    EXTRA_SHIFT_COMPENSATION_PLACEHOLDER: 'Enter Extra Shift Compensation',
    EXTRA_SHIFT_HOURS_LABEL: 'Extra Shift Hours',
    EXTRA_SHIFT_HOURS_PLACEHOLDER: 'Enter Extra Shift Hours',
    OTHER_COMPENSATION_LABEL: 'Other Compensation',
    OTHER_COMPENSATION_PLACEHOLDER: 'Enter Other Compensation',
    OTHER_COMPENSATION_CATEGORY_LABEL: 'Other Compensation Category',
    OTHER_COMPENSATION_CATEGORY_PLACEHOLDER: 'Enter Other Compensation Category',
    ANNUAL_UNRESTRICTED_ON_CALL_PAY_LABEL: 'Annual Unrestricted On-Call Pay',
    ANNUAL_UNRESTRICTED_ON_CALL_PAY_PLACEHOLDER: 'Enter Annual Unrestricted On-Call Pay',
    ANNUAL_UNRESTRICTED_ON_CALL_HOURS_LABEL: 'Annual Unrestricted On-Call Hours',
    ANNUAL_UNRESTRICTED_ON_CALL_HOURS_PLACEHOLDER: 'Enter Annual Unrestricted On-Call Hours',
    ANNUAL_RESTRICTED_ON_CALL_PAY_LABEL: 'Annual Restricted On-Call Pay',
    ANNUAL_RESTRICTED_ON_CALL_PAY_PLACEHOLDER: 'Enter Annual Restricted On-Call Pay',
    ANNUAL_RESTRICTED_ON_CALL_HOURS_LABEL: 'Annual Restricted On-Call Hours',
    ANNUAL_RESTRICTED_ON_CALL_HOURS_PLACEHOLDER: 'Enter Annual Restricted On-Call Hours',
  },
  LEADERSHIP_COMPENSATION: {
    TITLE: 'Leadership Compensation',
    BASE_COMPENSATION_LABEL: 'Leadership Base Compensation',
    BASE_COMPENSATION_PLACEHOLDER: 'Enter Leadership Base Compensation',
    INCENTIVE_COMPENSATION_LABEL: 'Leadership Incentive Compensation',
    INCENTIVE_COMPENSATION_PLACEHOLDER: 'Enter Leadership Incentive Compensation',
  },
  ACADEMIC_COMPENSATION: {
    TITLE: 'Academic Compensation',
    BASE_COMPENSATION_LABEL: 'Academic Base Compensation',
    BASE_COMPENSATION_PLACEHOLDER: 'Enter Academic Base Compensation',
    INCENTIVE_COMPENSATION_LABEL: 'Academic Incentive Compensation',
    INCENTIVE_COMPENSATION_PLACEHOLDER: 'Enter Academic Incentive Compensation',
  },

  PERSONALLY_PROVIDED_PRODUCTIVITY: {
    TITLE: 'Personally Provided Productivity',
    WRVUS_LABEL: '{wrvusOrAsaUnitsLabel}',
    WRVUS_PLACEHOLDER: 'Enter {wrvusOrAsaUnitsLabel}',
    PROFESSIONAL_COLLECTIONS_LABEL: 'Professional Collections',
    PROFESSIONAL_COLLECTIONS_PLACEHOLDER: 'Enter Professional Collections',
  },
  NOTE: {
    TITLE: 'Notes',
    NOTE_PLACEHOLDER: 'Enter Notes',
    ERROR: {
      NOTES_MAX_LENGTH: 'Notes cannot have more than 200 characters',
    },
  },
  DATAGRID: {
    TOGGLE: {
      SHOW_PERCENTILES: 'Show Percentiles',
      SHOW_MORE_PERCENTILES: 'Show more percentiles',
      SHOW_DETAILED_MARKET_DATA: 'Show Detailed Market Data',
    },
    HEADER: {
      TITLE: 'Total Cash Compensation (TCC) Analysis',
      NAME: 'Name:',
      SPECIALTY: 'Specialty:',
      MARKET_DATA_SOURCE: 'Market Data Source:',
    },
    FOOTER: {
      DISPLAY_SOURCE: `Source:`,
      NOTE: `<sup class="mr-0">*</sup>Exercise caution when interpreting market positioning results, as the smaller sample size can lead to greater year-over-year
            variability and larger data swings.`,
      DISCLAIMER_TEXT: `The results generated by this software are intended to represent a market assessment and should not be construed as a fair market value (FMV)
    opinion or an assessment of commercial reasonableness.<sup class="mr-0">1</sup> Users are advised to consult with qualified valuation and legal professionals for comprehensive and compliant FMV assessments. © {currentYear} by SullivanCotter. All rights reserved.`,
      SOURCE_NOTE_TEXT: `<sup class="mr-0">1</sup> Centers for Medicare & Medicaid Services (CMS), 'Medicare Program; Modernizing and Clarifying the Physician Self-Referral Regulations,' Federal Register,
    Vol. 85, No. 232, December 2, 2020, p. 77492.`,
    },
  },
  PRODUCTIVITY_ANALYSIS: `Productivity Analysis`,
  DETAILED_MARKET_DATA_AT_1_FTE: `Detailed Market Data at 1.0 FTE`,

  APPLY: 'Apply',
  PDF_EXPORT: {
    GUIDELINES: 'Quantitative Testing Guidelines',
    FOLLOWING_APPROACH: 'The following approach is used to assess physician total cash compensation:',

    CLINICAL_NONCLINICAL_UP_75:
      'Clinical and non-clinical TCC levels up to the 75th percentile of the market are within quantitative guidelines. In our experience, this threshold recognizes various aspects of physician performance, experience, and additional market factors.',

    EXAMPLES_NONCLINICAL: 'Examples of non-clinical services include medical directorships, research and academic activities.',

    EVALUATE_COMP_50_75:
      'Organizations should evaluate compensation arrangements with employed physicians when clinical TCC falls between the 50th and 75th percentiles with clinical productivity (wRVUs or professional collections) not within 25 percentile points of clinical TCC (e.g., clinical TCC at the 70th percentile with clinical productivity below the 45th percentile). Organizations should document the supporting business rationale for these arrangements. Organizations may choose to recalibrate clinical compensation such that it is better aligned with clinical productivity.',

    WHEN_TCC_EXCEEDS_75:
      'When clinical TCC exceeds the 75th percentile of the market, any of the following productivity tests can be used to determine if clinical TCC falls within quantitative guidelines:',

    TCC_PER_WRVU_RATIO: 'TCC per wRVU ratio at or below:',

    PRIMARY_CARE_65: '65th percentile for primary care (internal medicine, family medicine, and general pediatrics).',
    MED_SURG_60: '60th percentile for medical and surgical specialties.',
    HOSPITAL_55: '55th percentile for hospital-based specialties.',

    TCC_COLLECTIONS_RATIO_50: 'TCC to collections ratio at or below the 50th percentile.',
  },
  TOAST: {
    PDF_EXPORT_SUCCESS: {
      TITLE: 'PDF export success',
    },
    PDF_EXPORT_ERROR: {
      TITLE: 'PDF export error',
    },
  },
  EXPERT_REVIEW: {
    TITLE: 'Expert Review',
    TOOLTIP: 'Send market assessment report to SullivanCotter consultant for further review and discussion.',
    CONFIRMATION_TITLE: 'Expert Review Confirmation',
    CONFIRMATION_MESSAGE:
      'You are about to send this market assessment to the SullivanCotter consulting team for review. The information you submit cannot be edited after submission. Please ensure all details are accurate. Do you wish to continue?',
    SUCCESS_MESSAGE:
      'Your request has been submitted successfully. Expect an email from a SullivanCotter consultant within two business days with details on the next steps.',
    NOTES_TO_CONSULTANT_LABEL: 'Notes to Consultant',
    NOTES_TO_CONSULTANT_PLACEHOLDER: '',
    NOTES_TO_CONSULTANT_MAX_LENGTH: 200,
  },
  REPORT_NAME_MODAL: {
    SAVE: {
      TITLE: 'Save Report',
      INSTRUCTION_MESSAGE: 'Please provide a name for your Saved Report. The name must be less than 100 characters in length.',
    },
    EXPERT_REVIEW: {
      TITLE: 'Expert Review Report Name',
      INSTRUCTION_MESSAGE: 'Please provide a name for this report. The name must be less than 100 characters in length.',
    },
  },
  MARKET_DATA_SOURCE: 'Market Data Source:',
  SAVED_AND_SHARED_REPORTS: {
    TITLE: 'Saved and Shared Reports',
    SHARED_WITH_ME: 'Shared with Me',
    MY_SAVED_REPORTS: 'My Saved Reports',
    MY_SHARED_REPORTS: 'My Shared Reports',
    SAVED_DATE: 'Saved Date:',
    SHARED_DATE: 'Shared Date:',
    DELETE_CONFIRMATION_TITLE: 'Delete Report',
    DELETE_CONFIRMATION_MESSAGE: 'Are you sure you want to delete REPORT_NAME?',
    DELETE_SUCCESS_MESSAGE: 'REPORT_NAME was deleted successfully.',
    DELETE_DISABLED_TOOLTIP: "You'll be able to delete this report 90 days after sharing.",
    OWNER_DELETED_ALERT_MESSAGE: 'The user has deleted this shared report. Please reach out to them to determine if work is needed.',
    LOCKED_INPUTS_MESSAGE: 'This report has been shared with consulting and cannot be edited.',
    UNAVAILABLE_REPORT_LABEL: 'Unavailable report',
    UNAVAILABLE_REPORT_MESSAGE: 'Missing report metadata',
    UNAVAILABLE_REPORT_TOAST_TITLE: 'Report unavailable',
    UNAVAILABLE_REPORT_TOAST_MESSAGE: 'This saved or shared report is missing required metadata on the server and cannot be loaded.',
  },
  WRVUS_OR_ASA_UNITS_LABEL: 'wRVU or ASA Units',
  WORK_RVU_LABEL: 'Work RVU',
}

export const AT_GLANCE_REPORT = {
  TITLE: 'At-A-Glance Report',
  TOAST: {
    PDF_EXPORT_SUCCESS: {
      TITLE: 'PDF export success',
    },
    PDF_EXPORT_ERROR: {
      TITLE: 'PDF export error',
    },
  },
  ERROR: {
    FAILED_TO_CREATE_PDF: 'Error creating PDF:',
  },
  BREADCRUMBS: { BACK_BUTTON: 'Back', EXPORT_TO_PDF_BUTTON: 'Export to PDF' },
  DATE_CREATED: 'Date Created:',
  PROVIDER_INFORMATION: {
    TITLE: 'Provider Information',
    NAME_LABEL: 'Name:',
    NPI_OR_ID_LABEL: 'NPI or ID:',
    SPECIALTY_LABEL: 'Specialty:',
  },
  DATA_VARIABLES: {
    TITLE: 'Data Variables',
    COMPENSATION_PRINT_LABEL: 'Compensation (TCC):',
    COMPENSATION_LABEL: 'Compensation <br />TCC:',
    COMPENSATION_EXCLUDING_PREMIUM_LABEL: 'Compensation <br />TCC - Excluding Premium:',
    TOTAL_FTE_LABEL: 'Total FTE:',
    PRODUCTIVITY_LABEL: 'Productivity ({wrvusOrAsaUnitsLabel}):',
    CLINICAL_FTE: 'Clinical FTE (cFTE):',
  },
  NOTES: {
    TITLE: 'Notes',
  },
  PLOT_TEXT: {
    DESCRIPTION: `Compensation market benchmarks are Total FTE adjusted and Productivity market benchmarks are cFTE adjusted based on incumbent Data
          Variables submitted.`,
    DISCLAIMER_LINE_1_TEXT: `The results generated by this software are intended to represent a market assessment and should not be construed as a fair market value
          (FMV) opinion or an assessment of commercial reasonableness.&#185; Users are advised to consult with qualified valuation and legal
          professionals for comprehensive and compliant FMV assessments. &copy; {currentYear} by SullivanCotter. All rights reserved.
          <br />
          <br />`,
    DISCLAIMER_LINE_2_TEXT: `&#185; Centers for Medicare & Medicaid Services (CMS), 'Medicare Program; Modernizing and Clarifying the Physician Self-Referral
            Regulations,' Federal Register, Vol. 85, No. 232, December 2, 2020, p. 77492.`,
  },
  WRVUS_OR_ASA_UNITS_LABEL: 'wRVU or ASA Units',
}

export const SURVEY_REPORT = {
  FILTERS: 'Filters',
  MARKET_DATA_SOURCE: 'Market Data Source',
  SPECIALTY_GROUP: 'Specialty Group',
  SPECIALTY: 'Specialty',
  DATA_VARIABLE: 'Data Variable',
  PERCENTILES: 'Percentiles',
  APPLY: 'Apply',
  PLEASE_SELECT_DATA_SOURCE: 'Please select a <b>Market Data Source</b> to see data.',
  VIEW_BY_SPECIALTY: 'View by Specialty',
  VIEW_BY_DATA_VARIABLE: 'View by Data Variable',
  NO_COMBINATION_MESSAGE: 'No data is available for the selected data variable. Please select a different data variable to view the Survey Report',
}
